(() => {
  'use strict';
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const FAMILY_NAMES = Object.freeze({
    'Atera Signo': 'Atera Roofbars', 'Atera Signo Black': 'Atera Roofbars',
    'Cruz Airo Fuse': 'Cruz Roofbars', 'Cruz Airo R Dark': 'Cruz Roofbars',
    'Cruz Airo T': 'Cruz Roofbars', 'Cruz Airo X Dark': 'Cruz Roofbars',
    'Cruz Cargo Aluminium': 'Cruz Roofbars', 'Cruz Cargo Aluminium Black': 'Cruz Roofbars',
    'Cruz Cargo Steel': 'Cruz Roofbars', 'Cruz Oplus S-FIX': 'Cruz Roofbars',
    'Cruz Oplus SR+': 'Cruz Roofbars', 'Cruz Oplus ST': 'Cruz Roofbars',
    'Cruz Oplus SX': 'Cruz Roofbars', 'Green Valley Edge': 'Green Valley Roofbars',
    'Green Valley Trax': 'Green Valley Roofbars', 'Menabo Dozer XXL': 'Menabo Roofbars',
    'Menabo Omega Steel': 'Menabo Roofbars', 'Menabo Professional': 'Menabo Roofbars',
    'Menabo Sherman': 'Menabo Roofbars', 'Menabo Tiger': 'Menabo Roofbars',
    'Menabo Tiger Black': 'Menabo Roofbars'
  });
  const FALLBACK_SKU = 'atera-signo-200';
  let legacyPromise;
  let productPromise;
  const getJson = async url => {
    const response = await fetch(url, {credentials: 'same-origin'});
    if (!response.ok) throw new Error(`Fitment data HTTP ${response.status}`);
    return response.json();
  };
  const legacyData = (optionsUrl, resultsUrl) => {
    if (!legacyPromise) legacyPromise = Promise.all([getJson(optionsUrl), getJson(resultsUrl)])
      .then(([options, results]) => {
        if (!options?.options || !results?.results || options.meta?.selector_version !== results.meta?.selector_version)
          throw new Error('Legacy fitment dataset mismatch');
        return {options: options.options, results: results.results};
      }).catch(error => {legacyPromise = null; throw error});
    return legacyPromise;
  };
  const productData = url => {
    if (!productPromise) productPromise = getJson(url).catch(error => {productPromise = null; throw error});
    return productPromise;
  };
  const uniqueKey = (object, value) => {
    const matches = Object.keys(object || {}).filter(key => normalize(key) === normalize(value));
    return matches.length === 1 ? matches[0] : null;
  };
  const findLegacy = (data, vehicle) => {
    const make = uniqueKey(data.options, vehicle.make);
    if (!make) return null;
    const models = data.options[make];
    const model = uniqueKey(models, vehicle.model) ||
      (vehicle.variant ? uniqueKey(models, `${vehicle.model} ${vehicle.variant}`) : null);
    if (!model) return null;
    const variants = models[model];
    const variant = uniqueKey(variants, vehicle.variant) ||
      (!vehicle.variant && Object.keys(variants).length === 1 && Object.hasOwn(variants, 'No Variant') ? 'No Variant' : null) ||
      (normalize(model) === normalize(`${vehicle.model} ${vehicle.variant}`) &&
        Object.keys(variants).length === 1 && Object.hasOwn(variants, 'No Variant') ? 'No Variant' : null);
    if (!variant) return null;
    const year = String(vehicle.year);
    if (!(variants[variant]?.[year] || []).includes(vehicle.roof_type)) return null;
    const result = data.results[make]?.[model]?.[variant]?.[year]?.[vehicle.roof_type];
    if (!result) return null;
    if (!result.shopify_variant_key || !FAMILY_NAMES[result.shopify_variant_title])
      throw new Error(`Unmapped legacy roof-bar family: ${result.shopify_variant_title || 'unknown'}`);
    return result;
  };
  const imageFor = (variant, product) => variant.featured_media?.preview_image?.src ||
    variant.featured_image?.src || (product.variants.length === 1 ?
      (product.featured_image || product.media?.[0]?.preview_image?.src || '') : '');
  const resolve = async (vehicle, urls) => {
    let data = null;
    try {data = await legacyData(urls.options, urls.results)}
    catch (error) {console.warn('Legacy fitment unavailable; using Atera fallback', error)}
    const legacy = data ? findLegacy(data, vehicle) : null;
    const product = await productData(urls.product);
    if (!product?.id || !Array.isArray(product.variants) || product.handle !== 'roof-bars')
      throw new Error('Shopify roof-bar product unavailable');
    const sku = legacy?.shopify_variant_key || FALLBACK_SKU;
    const variants = product.variants.filter(item => item.sku === sku);
    if (variants.length !== 1) throw new Error(`Shopify roof-bar SKU unresolved: ${sku}`);
    const variant = variants[0];
    if (!Number.isInteger(variant.price) || variant.price <= 0 || !variant.id)
      throw new Error(`Shopify price or variant missing: ${sku}`);
    const image = imageFor(variant, product);
    // This legacy field is the old selector's published dynamic/roof-bar load, not a static load.
    // The fallback uses the same Atera Signo 001 SKU's legacy load record only when consistent.
    let dynamicLoad = Number(legacy?.max_load_kg_num);
    let loadSource = legacy && dynamicLoad > 0 ? 'legacy_result.max_load_kg_num' : '';
    if (!legacy) {
      const ateraLoads = new Set();
      try {
        const data = await legacyData(urls.options, urls.results);
        for (const models of Object.values(data.results)) for (const variantsByModel of Object.values(models))
          for (const years of Object.values(variantsByModel)) for (const roofs of Object.values(years))
            for (const item of Object.values(roofs)) if (item.shopify_variant_key === FALLBACK_SKU)
              ateraLoads.add(Number(item.max_load_kg_num));
      } catch (_) { /* Missing legacy data cannot establish a fallback load. */ }
      dynamicLoad = ateraLoads.size === 1 ? [...ateraLoads][0] : NaN;
      if (dynamicLoad > 0) loadSource = 'legacy_atera_signo_001.max_load_kg_num';
    }
    return {
      source: legacy ? 'legacy_selector' : 'fallback_atera',
      legacyKey: legacy?.selection_key || '', family: legacy ? FAMILY_NAMES[legacy.shopify_variant_title] : 'Atera Roofbars',
      productId: product.id, variantId: variant.id, sku, productTitle: product.title, variantTitle: variant.title,
      url: `${product.url}?variant=${variant.id}`, image, priceCents: variant.price,
      currency: globalThis.Shopify?.currency?.active || 'EUR', available: Boolean(variant.available),
      dynamicLoad: Number.isFinite(dynamicLoad) && dynamicLoad > 0 ? dynamicLoad : null,
      loadSource
    };
  };
  window.FitmentGlobalLegacy = {resolve, FAMILY_NAMES};
})();
